import React, { useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';
import { withViewport } from '../../../util/uiHelpers';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/** Get intrinsic dimensions from image variant for reserving space (prevents layout shift) */
const getImageDimensions = (image, variantName) => {
  const v = image?.attributes?.variants?.[variantName];
  return v?.width && v?.height ? { width: v.width, height: v.height } : null;
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGalleryComponent = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants, viewport } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1400px) calc(100vw - 192px), 1200px` };

  const renderItem = item => {
    const imgDimensions = getImageDimensions(item.image, imageVariants[1]);
    // Reserve space via img width/height attributes (prevents layout shift without constraining image)
    const responsiveImageProps = {
      rootClassName: css.item,
      image: item.image,
      alt: item.alt,
      variants: imageVariants,
      width: imgDimensions?.width,
      height: imgDimensions?.height,
      ...imageSizesMaybe,
    };

    const content = (
      <div className={css.itemCentering}>
        <ResponsiveImage {...responsiveImageProps} />
      </div>
    );

    if (viewport?.width > 768) {
      // Desktop: img width/height reserves space, image displays at natural size
      return content;
    }

    // Mobile: use AspectRatioWrapper only when dimensions not yet available (fallback for SSR/hydration)
    if (!imgDimensions) {
      return (
        <AspectRatioWrapper
          width={aspectWidth || 1}
          height={aspectHeight || 1}
          className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        >
          {content}
        </AspectRatioWrapper>
      );
    }

    // When we have dimensions, img width/height reserves space - no AspectRatioWrapper needed
    return content;
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={() => null}
      onScreenChange={onScreenChange}
      renderLeftNav={() => null}
      renderRightNav={() => null}
      renderFullscreenButton={() => null}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

const ListingImageGallery = withViewport(ListingImageGalleryComponent);

export default ListingImageGallery;
